import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowLeftRight, ArrowRight, BookOpen, Check, ChevronDown, ChevronRight, GraduationCap, Layers, LayoutDashboard,
  LogOut, Monitor, Moon, Newspaper, NotebookPen, RotateCcw, Route, Search, SlidersHorizontal, Sparkles, Sun, Target, Trophy,
  Crown, ShieldCheck, UserRound, Users,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { adminService, articleReviewService } from '../../services/api';
import { useGamification } from '../../contexts/GamificationContext';
import { fmtXp } from '../../utils/gamification';
import { useTheme } from '../../contexts/ThemeContext';
import { Brand } from './Brand';
import { SectionTag } from './Badge';
import { IconBadge } from './Card';
import { USO } from '../tour/script';
import { NotificationBell } from '../notifications/NotificationBell';

const USO_STEPS = USO.reduce((n, c) => n + c.steps.length + (c.intro ? 1 : 0), 0);

const COURSE_NAV = [
  { to: '', label: 'Visão geral', icon: LayoutDashboard, end: true },
  { to: '/estudo', label: 'Estudo', icon: BookOpen, tour: 'curso-tabs:estudo' },
  { to: '/simulados', label: 'Simulados', icon: Target, also: ['/config', '/exam', '/results'] },
  { to: '/flashcards', label: 'Flashcards', icon: Layers },
  { to: '/erros', label: 'Erros', icon: NotebookPen },
  { to: '/conquistas', label: 'Conquistas', icon: Trophy, dot: 'unseen' },
  { to: '/buscar', label: 'Buscar', icon: Search },
];

/**
 * Estrutura das páginas: barra superior (marca, curso atual e menu do usuário)
 * e, dentro de um curso, o menu de seções.
 */
export function AppShell({ section, sectionNum, courseName, children }) {
  const navigate = useNavigate();
  const { courseSlug } = useParams();
  const base = courseSlug ? `/curso/${courseSlug}` : null;
  const gamification = useGamification();
  const xp = gamification?.slug === courseSlug ? gamification.summary : null;

  return (
    <div className="ss-shell">
      <header className="ss-topbar">
        <DemoBar />
        <div className="ss-topbar__inner">
          <div className="ss-topbar__left">
            <button type="button" className="ss-topbar__brand" onClick={() => navigate('/')} aria-label="Início" data-tour="brand">
              <Brand size="sm" />
            </button>
            <GlobalNav inCourse={!!base} />
          </div>
          <div className="ss-topbar__right">
            {!base && section && <SectionTag num={sectionNum} label={section} className="ss-hide-mobile" />}
            {base && <XpPill courseSlug={courseSlug} />}
            {courseName && (
              <button type="button" className="ss-topbar__course ss-topbar__course--curso" onClick={() => navigate('/')} title="Trocar de curso">
                <ArrowLeftRight size={15} strokeWidth={2.2} aria-hidden="true" />
                <span>{courseName}</span>
              </button>
            )}
            <NotificationBell />
            <UserMenu />
          </div>
        </div>
        {base && (
          <nav className="ss-subnav" aria-label="Seções do curso" data-tour="curso-tabs">
            <div className="ss-subnav__inner">
              {COURSE_NAV.map(item => (
                <NavLink
                  key={item.label}
                  to={base + item.to}
                  end={item.end}
                  data-tour={item.tour}
                  className={({ isActive }) => {
                    const alsoActive = item.also?.some(p => window.location.pathname.startsWith(base + p));
                    return `ss-subnav__link ${isActive || alsoActive ? 'is-active' : ''}`;
                  }}
                >
                  <item.icon size={16} strokeWidth={2.2} aria-hidden="true" />
                  <span>{item.label}</span>
                  {item.dot && xp?.[item.dot] && (
                    <span className="ss-subnav__dot" role="img" aria-label="medalha nova" />
                  )}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </header>
      <main className="ss-main">{children}</main>
    </div>
  );
}

/** Nível e XP no curso; leva à página de conquistas. */
function XpPill({ courseSlug }) {
  const gamification = useGamification();
  const navigate = useNavigate();
  const refresh = gamification?.refresh;

  // Cada página monta o próprio AppShell: atualiza a cada navegação
  useEffect(() => { refresh?.(courseSlug); }, [courseSlug, refresh]);

  if (!gamification || gamification.slug !== courseSlug || !gamification.summary) return null;
  const { level } = gamification.summary;
  return (
    <button
      type="button"
      className="ss-xppill"
      onClick={() => navigate(`/curso/${courseSlug}/conquistas`)}
      title="Ver conquistas"
      aria-label={`Nível ${level.level}, ${fmtXp(level.xp)} XP no curso. Ver conquistas`}
    >
      <span className="ss-xppill__level">{level.level}</span>
      <span className="ss-xppill__info">
        <span className="ss-xppill__xp">{fmtXp(level.xp)} XP</span>
        <span className="ss-xppill__bar"><span style={{ width: `${level.pct}%` }} /></span>
      </span>
    </button>
  );
}

const THEMES = [
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark', label: 'Escuro', icon: Moon },
  { value: 'system', label: 'Sistema', icon: Monitor },
];

const GLOBAL_NAV = [
  { to: '/cursos', label: 'Cursos', icon: GraduationCap, tour: 'cursos' },
  { to: '/trilhas', label: 'Trilhas', icon: Route, tour: 'trilhas' },
  { to: '/artigos', label: 'Artigos', icon: Newspaper, tour: 'artigos' },
];

/** Cursos/Trilhas/Artigos no topo. Dentro de um curso, "Cursos" fica ativo. */
function GlobalNav({ inCourse = false }) {
  return (
    <nav className="ss-globalnav" aria-label="Seções">
      {GLOBAL_NAV.map(item => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-tour={item.tour}
          className={({ isActive }) => `ss-globalnav__link ${isActive || (inCourse && item.to === '/cursos') ? 'is-active' : ''}`}
        >
          <item.icon size={16} strokeWidth={2.2} aria-hidden="true" />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

/** Estrutura das páginas fora de curso (trilhas e artigos): marca, Cursos/Trilhas/Artigos e o menu do usuário.
 *  Dentro de uma trilha, `trail` mostra a trilha atual à direita, como o curso no AppShell. */
export function GlobalShell({ trail, children }) {
  const navigate = useNavigate();
  return (
    <div className="ss-shell">
      <header className="ss-topbar">
        <DemoBar />
        <div className="ss-topbar__inner">
          <div className="ss-topbar__left">
            <button type="button" className="ss-topbar__brand" onClick={() => navigate('/')} aria-label="Início" data-tour="brand">
              <Brand size="sm" />
            </button>
            <GlobalNav />
          </div>
          <div className="ss-topbar__right">
            {trail && (
              <button type="button" className="ss-topbar__course" onClick={() => navigate('/trilhas')} title="Trocar de trilha">
                <ArrowLeftRight size={15} strokeWidth={2.2} aria-hidden="true" />
                <span>{trail.shortTitle || trail.title}</span>
              </button>
            )}
            <NotificationBell />
            <UserMenu />
          </div>
        </div>
      </header>
      <main className="ss-main">{children}</main>
    </div>
  );
}

/** Aviso fixo na conta de demonstração. */
export function DemoBar() {
  const { user, logout } = useAuth();
  if (!user?.demo) return null;
  return (
    <div className="ss-demobar" role="note">
      <Sparkles size={14} aria-hidden="true" />
      <span>
        <strong>Demonstração</strong>
        <span className="ss-hide-mobile"> · progresso fictício, recriado todo dia</span>
      </span>
      <button type="button" onClick={logout}>Sair da demonstração</button>
    </div>
  );
}

export function UserMenu() {
  const { user, isAdmin, isSuperAdmin, logout } = useAuth();
  const { preference, setPreference } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(0);
  const ref = useRef(null);

  // Solicitações de cadastro esperando o super admin: contador em "Usuários" (o aviso em si vai pelo sininho)
  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    const refresh = () => adminService.pendingRequests().then(setPending).catch(() => setPending(0));
    refresh();
    // A tela de Usuários avisa quando aprova ou recusa
    window.addEventListener('access-requests:changed', refresh);
    return () => window.removeEventListener('access-requests:changed', refresh);
  }, [isSuperAdmin, location.pathname]);

  // Artigos públicos esperando um admin: contador vermelho em "Revisão de artigos"
  const [reviewsPending, setReviewsPending] = useState(0);
  useEffect(() => {
    if (!isAdmin || user?.demo) return undefined;
    const refresh = () => articleReviewService.pendingCount().then(setReviewsPending).catch(() => setReviewsPending(0));
    refresh();
    window.addEventListener('article-reviews:changed', refresh);
    return () => window.removeEventListener('article-reviews:changed', refresh);
  }, [isAdmin, user?.demo, location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;
  const initials = user.name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
  const go = (path) => { setOpen(false); navigate(path); };
  // Itens novos levam o selo "Novo" até o primeiro clique
  const seen = (key) => { try { return localStorage.getItem(`menu-seen:${key}`) === '1'; } catch { return true; } };
  const markSeen = (key) => { try { localStorage.setItem(`menu-seen:${key}`, '1'); } catch { /* sem storage */ } };
  const openPrefs = () => { markSeen('prefs'); go('/preferencias'); };
  // Abre o índice de capítulos do tour (continuar, pular para um capítulo ou recomeçar)
  const restartTour = () => {
    markSeen('tour');
    setOpen(false);
    window.dispatchEvent(new CustomEvent('tour:index'));
  };
  const tourSeenSteps = Math.min(USO_STEPS, user.tour?.uso?.step || (user.tour?.uso?.done ? USO_STEPS : 0));
  const roleSuffix = isSuperAdmin ? ' · super admin' : isAdmin ? ' · admin' : '';
  const student = !isAdmin && !user.demo;

  return (
    <div className="ss-usermenu" ref={ref} data-tour="usermenu">
      <button
        type="button"
        className="ss-usermenu__trigger"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Menu da conta de ${user.name}`}
      >
        <Avatar user={user} initials={initials} />
        <ChevronDown size={15} aria-hidden="true" className="ss-hide-mobile" />
      </button>
      {open && (
        <div className="ss-usermenu__panel" role="menu">
          <div className="ss-usermenu__who">
            <strong>{user.name}</strong>
            <span>{user.demo ? 'conta de demonstração' : user.username}{roleSuffix}</span>
          </div>
          <button type="button" role="menuitem" onClick={() => go('/conta')}>
            <UserRound size={16} aria-hidden="true" /> Minha conta
          </button>
          {student && (
            <button type="button" role="menuitem" onClick={openPrefs}>
              <SlidersHorizontal size={16} aria-hidden="true" /> Preferências de estudo
              {!seen('prefs') && <span className="ss-newpill ss-newpill--end">Novo</span>}
            </button>
          )}
          <button type="button" role="menuitem" onClick={restartTour}>
            <RotateCcw size={16} aria-hidden="true" /> Refazer tour
            <span className="ss-usermenu__progress" aria-label={`${tourSeenSteps} de ${USO_STEPS} passos vistos`}>{tourSeenSteps}/{USO_STEPS}</span>
          </button>
          {isAdmin && !user.demo && (
            <>
              <span className="ss-usermenu__section">Administração</span>
              {isSuperAdmin && (
                <button type="button" role="menuitem" onClick={() => go('/admin/usuarios')}>
                  <Users size={16} aria-hidden="true" /> Usuários
                  {pending > 0 && <span className="ss-usermenu__count">{pending}</span>}
                </button>
              )}
              <button type="button" role="menuitem" onClick={() => go('/admin/revisao')}>
                <ShieldCheck size={16} aria-hidden="true" /> Revisão de artigos
                {reviewsPending > 0 && <span className="ss-usermenu__count">{reviewsPending}</span>}
              </button>
              {isSuperAdmin && (
                <button type="button" role="menuitem" onClick={() => go('/admin/ia')}>
                  <Sparkles size={16} aria-hidden="true" /> Inteligência artificial
                  <Crown size={14} aria-hidden="true" className="ss-usermenu__crown" />
                </button>
              )}
            </>
          )}
          <div className="ss-usermenu__theme" role="group" aria-label="Tema">
            {THEMES.map(t => (
              <button
                key={t.value}
                type="button"
                className={preference === t.value ? 'is-active' : ''}
                aria-pressed={preference === t.value}
                onClick={() => setPreference(t.value)}
                title={`Tema ${t.label.toLowerCase()}`}
              >
                <t.icon size={15} aria-hidden="true" />
                <span>{t.label}</span>
              </button>
            ))}
          </div>
          <button type="button" role="menuitem" className="ss-usermenu__logout" onClick={() => { setOpen(false); logout(); }}>
            <LogOut size={16} aria-hidden="true" /> Sair
          </button>
        </div>
      )}
    </div>
  );
}

/** Foto do perfil ou as iniciais. */
export function Avatar({ user, initials, size, className = '' }) {
  const style = size ? { width: size, height: size, fontSize: Math.round(size * 0.36) } : undefined;
  if (user?.avatarUrl) {
    return <img className={`ss-avatar ss-avatar--img ${className}`} src={user.avatarUrl} alt="" style={style} />;
  }
  return <span className={`ss-avatar ${className}`} aria-hidden="true" style={style}>{initials}</span>;
}

// Cabeçalho de página: ícone, título em Oswald e ações.
export function PageHeader({ icon, eyebrow, title, subtitle, actions, children }) {
  return (
    <div className="ss-pageheader">
      <div className="ss-pageheader__main">
        {icon && <IconBadge icon={icon} size={56} />}
        <div className="ss-pageheader__text">
          {eyebrow && <span className="ss-eyebrow">{eyebrow}</span>}
          <h1 className="ss-title">{title}</h1>
          {subtitle && <p className="ss-pageheader__subtitle">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="ss-pageheader__actions">{actions}</div>}
      {children}
    </div>
  );
}

// Trilha de navegação: items = [{ label, onClick }], o último é a página atual.
export function Breadcrumbs({ items }) {
  return (
    <nav className="ss-breadcrumbs" aria-label="Você está em">
      {items.map((item, i) => {
        const last = i === items.length - 1;
        return (
          <React.Fragment key={i}>
            {last || !item.onClick ? (
              <span className={last ? 'ss-breadcrumbs__current' : ''} aria-current={last ? 'page' : undefined}>
                {item.label}
              </span>
            ) : (
              <button type="button" onClick={item.onClick}>{item.label}</button>
            )}
            {!last && <ChevronRight size={14} aria-hidden="true" className="ss-breadcrumbs__sep" />}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

// Navegação anterior/próximo no fim das páginas de conteúdo.
export function PagerNav({ prev, next }) {
  return (
    <nav className="ss-pager" aria-label="Navegação">
      {prev ? (
        <button type="button" className="ss-pager__item" onClick={prev.onClick}>
          <ArrowLeft size={18} aria-hidden="true" />
          <span>
            <span className="ss-pager__hint">{prev.hint || 'Anterior'}</span>
            <span className="ss-pager__label">{prev.label}</span>
          </span>
        </button>
      ) : <span />}
      {next && (
        <button
          type="button"
          className={`ss-pager__item ss-pager__item--next ${next.done ? 'ss-pager__item--done' : ''}`}
          onClick={next.onClick}
        >
          <span>
            <span className="ss-pager__hint">{next.hint || 'Próximo'}</span>
            <span className="ss-pager__label">{next.label}</span>
          </span>
          {next.done ? <Check size={18} aria-hidden="true" /> : <ArrowRight size={18} aria-hidden="true" />}
        </button>
      )}
    </nav>
  );
}

// Título de seção dentro da página.
export function SectionTitle({ icon: Icon, iconTone, children, aside, id }) {
  return (
    <div className="ss-sectiontitle" id={id}>
      <h2>
        {Icon && <Icon size={15} strokeWidth={2.2} aria-hidden="true" className={`ss-sectiontitle__icon ${iconTone ? `is-${iconTone}` : ''}`} />}
        {children}
      </h2>
      {aside && <div className="ss-sectiontitle__aside">{aside}</div>}
    </div>
  );
}
